import { HStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { clsx } from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCheckBulkTradeWithTradeCapture } from '@/App/BulkTrade/BulkTradeContext.tsx';
import { useDisabledDatePresets } from '@/App/tiles/shared/useDisabledDatePresets.tsx';
import { SgIcon } from '@/components/sg-design-system/components/display/SgIcon';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton';
import { SgCheckbox } from '@/components/sg-design-system/components/input/SgCheckbox';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { SgPicker } from '@/components/sg-design-system/components/input/SgPicker';
import type { SgPickerItem } from '@/components/sg-design-system/components/input/SgPicker/types.ts';
import { DatePickerWithTenorNext } from '@/components/ui/DatePickerWithTenorNext.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { bulkTradeSlice } from '@/store/bulkTrade/slice.ts';
import type { BulkLeg, BulkTradeId, TabId } from '@/store/bulkTrade/state.ts';
import { isSwapLeg } from '@/store/bulkTrade/utilities/guards.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import type { ISODate, Tenor } from '@/types/date.ts';
import { BUY_BACKGROUND_COLOR, BUY_COLOR, SELL_BACKGROUND_COLOR, SELL_COLOR } from '@/utils/colors.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

type BulkCreationTableRowProps = {
  tabId: TabId;
  bulkTradeId: BulkTradeId;
  productId: string;
  product: BulkLeg;
  productNameByProductId: Record<string, { type: BulkLeg['type'] }>;
  negotiatedCurrencyIndex: CurrencyIndex;
  currency1: string;
  currency2: string;
  isSelected: boolean;
  toggleSelection: (productId: string) => void;
  isExcluded: boolean;
  isRfsPending: boolean;
};

export const BulkCreationTableRow = (props: BulkCreationTableRowProps) => {
  const {
    tabId,
    bulkTradeId,
    productId,
    product,
    productNameByProductId,
    negotiatedCurrencyIndex,
    currency1,
    currency2,
    isSelected,
    toggleSelection,
    isExcluded,
    isRfsPending,
  } = props;

  const dispatch = useAppDispatch();

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { formatMessage } = useIntl();
  const checkBulkTradeWithTradeCapture = useCheckBulkTradeWithTradeCapture();

  const disabledDatePresets = useDisabledDatePresets(`${tabId}/BULK_TRADE`, `${currency1}/${currency2}`);

  const allProductTypes = [
    { value: 'Spot' as const, label: formatMessage({ id: 'bulkTrade.spot' }) },
    { value: 'Forward' as const, label: formatMessage({ id: 'bulkTrade.forward' }) },
    { value: 'Swap' as const, label: formatMessage({ id: 'bulkTrade.swap' }) },
  ];

  const handleProductChange = ({ value: productType }: SgPickerItem<'Spot' | 'Forward' | 'Swap'>) => {
    dispatch(
      bulkTradeSlice.actions.changeLegProduct({
        tabId,
        legId: productId,
        productName: productType,
      }),
    );

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      negotiatedCurrencyIndex,
      legChanges: {
        ...productNameByProductId,

        [productId]: {
          type: productType,

          // ...(productType === 'Swap' ? { nearNotional: amount1, nearExpiryDate: 'SP' } : { notional: amount1 }),
        },
      },
    });
  };

  const handleTooggleUneven = () => {
    if (!isSwapLeg(product)) {
      return;
    }

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      negotiatedCurrencyIndex,
      legChanges: {
        ...productNameByProductId,

        [productId]: {
          type: 'Swap',
          isUneven: !product.isUneven,
        },
      },
    });
  };

  const negotiatedCurrency = negotiatedCurrencyIndex === 1 ? currency1 : currency2;

  const isBuyFromClientPerspective =
    ((product.way === 'Buy' || product.way === 'BuyThenSell') && negotiatedCurrencyIndex === 1) ||
    ((product.way === 'Sell' || product.way === 'SellThenBuy') && negotiatedCurrencyIndex === 2);

  const wayLabelId = isSwapLeg(product)
    ? isBuyFromClientPerspective
      ? 'bulkTrade.way.clientBuysAndSells'
      : 'bulkTrade.way.clientSellsAndBuys'
    : isBuyFromClientPerspective
      ? 'bulkTrade.way.clientBuys'
      : 'bulkTrade.way.clientSells';

  const way = formatMessage({ id: wayLabelId }, { currency: negotiatedCurrency });

  const handleToggleWay = () => {
    if (isRfsPending) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'toggle way');

    if (product.type === 'Swap') {
      checkBulkTradeWithTradeCapture({
        bulkTradeId,
        negotiatedCurrencyIndex,
        legChanges: {
          ...productNameByProductId,

          [productId]: {
            type: product.type,
            way: product.way === 'BuyThenSell' ? 'SellThenBuy' : 'BuyThenSell',
          },
        },
      });

      return;
    }

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      negotiatedCurrencyIndex,
      legChanges: {
        ...productNameByProductId,

        [productId]: {
          type: product.type,
          way: product.way === 'Buy' ? 'Sell' : 'Buy',
        },
      },
    });
  };

  const amount1 = product.type === 'Swap' ? product.nearNotional : product.notional;
  const amount1Errors = (product.type === 'Swap' ? product.nearNotionalErrors : product.notionalErrors) ?? [];

  const handleAmount1Change = (value: number | undefined) => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'change amount 1');

    if (product.type === 'Swap') {
      checkBulkTradeWithTradeCapture({
        bulkTradeId,
        negotiatedCurrencyIndex,
        legChanges: {
          ...productNameByProductId,

          [productId]: {
            type: product.type,
            nearNotional: value ?? null,
          },
        },
      });
    }

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      negotiatedCurrencyIndex,
      legChanges: {
        ...productNameByProductId,

        [productId]: {
          type: product.type,
          notional: value ?? null,
        },
      },
    });
  };

  const handleToggleCurrency = () => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'toggle currency');

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      negotiatedCurrencyIndex: negotiatedCurrencyIndex === 1 ? 2 : 1,
      legChanges: {
        ...productNameByProductId,

        [productId]: {
          type: product.type,
        },
      },
    });
  };

  const date1 = product.type === 'Swap' ? product.nearExpiryDate : product.maturityDate;
  const dateTenor1 = product.type === 'Swap' ? product.nearExpiryDateTenor : product.maturityDateTenor;
  const date1Errors = (product.type === 'Swap' ? product.nearExpiryDateErrors : product.maturityDateErrors) ?? [];

  const handleDate1Change = (date: ISODate | Tenor | undefined) => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'change date 1');

    if (product.type === 'Swap') {
      checkBulkTradeWithTradeCapture({
        bulkTradeId,
        negotiatedCurrencyIndex,
        legChanges: {
          ...productNameByProductId,

          [productId]: {
            type: product.type,
            nearExpiryDate: date,
          },
        },
      });
    }

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      negotiatedCurrencyIndex,
      legChanges: {
        ...productNameByProductId,

        [productId]: {
          type: product.type,
          maturityDate: date,
        },
      },
    });
  };

  const amount2 = product.type === 'Swap' ? product.farNotional : undefined;
  const amount2Errors = (product.type === 'Swap' ? product.farNotionalErrors : undefined) ?? [];

  const handleAmount2Change = (value: number | undefined) => {
    if (!isSwapLeg(product)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'change amount 2');

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      negotiatedCurrencyIndex,
      legChanges: {
        ...productNameByProductId,

        [productId]: {
          type: 'Swap',
          farNotional: value ?? null,
        },
      },
    });
  };

  const date2 = product.type === 'Swap' ? product.farExpiryDate : undefined;
  const dateTenor2 = product.type === 'Swap' ? product.farExpiryDateTenor : undefined;
  const date2Errors = (product.type === 'Swap' ? product.farExpiryDateErrors : undefined) ?? [];

  const handleDate2Change = (date: ISODate | Tenor | undefined) => {
    if (!isSwapLeg(product)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'change date 2');

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      negotiatedCurrencyIndex,
      legChanges: {
        ...productNameByProductId,

        [productId]: {
          type: 'Swap',
          farExpiryDate: date,
        },
      },
    });
  };

  return (
    <tr data-e2e="product-row">
      <td className="py-0">
        <SgCheckbox
          dataE2e="checkbox"
          checked={isSelected}
          onClick={() => toggleSelection(productId)}
          readOnly={isRfsPending}
        />
      </td>

      <td className="p-0 d-flex align-items-center" style={{ opacity: isExcluded ? 0.5 : undefined }}>
        <SgPicker<'Spot' | 'Forward' | 'Swap'>
          itemSelection="single"
          selectedValues={[product.type]}
          items={allProductTypes}
          showIcon={false}
          onChange={handleProductChange}
          placeholder="Cross currency"
          showClearButton={false}
          size="md"
          data-e2e="product-name"
          classes={clsx('flex-grow-1 flex-auto flex-shrink-1')}
          readonly={isRfsPending}
        />

        {isSwapLeg(product) && (
          <SgButton
            data-e2e="uneven"
            onClick={handleTooggleUneven}
            color={product.isUneven ? 'warning' : 'primary'}
            disabled={isRfsPending}
          >
            {product.isUneven ? <FormattedMessage id="bulkTrade.uneven" /> : <FormattedMessage id="bulkTrade.even" />}
          </SgButton>
        )}
      </td>

      <td
        style={{
          opacity: isExcluded ? 0.5 : undefined,
          background: isBuyFromClientPerspective ? BUY_BACKGROUND_COLOR : SELL_BACKGROUND_COLOR,
          color: isBuyFromClientPerspective ? BUY_COLOR : SELL_COLOR,
        }}
      >
        <HStack
          horizontalAlignment="start"
          verticalAlignment="center"
          gap="4px"
          data-e2e="way"
          onClick={handleToggleWay}
          style={{ height: 20 }}
        >
          {way}
          <SgIcon icon="repeat" size="sm" />
        </HStack>
      </td>

      <td className="p-0" style={{ opacity: isExcluded ? 0.5 : undefined }}>
        <TradeCaptureValidationWrapper errors={amount1Errors}>
          <SgNumberInput
            size="md"
            value={amount1}
            valuePosition="start"
            maxDecimals={9}
            selectAllOnFocus
            acceleratorsEnabled
            allowNegative={false}
            onChange={handleAmount1Change}
            unit={negotiatedCurrency}
            unitBackgroundColor={negotiatedCurrencyIndex === 1 ? undefined : 'warning'}
            unitPosition="end"
            unitTabNoIndex
            onUnitClick={handleToggleCurrency}
            data-e2e="amount-1"
            state={amount1Errors.length > 0 ? 'invalid' : undefined}
            readOnly={isRfsPending}
          />
        </TradeCaptureValidationWrapper>
      </td>

      <td className="p-0" style={{ opacity: isExcluded ? 0.5 : undefined }}>
        <TradeCaptureValidationWrapper errors={date1Errors}>
          <DatePickerWithTenorNext
            date={date1}
            tenor={dateTenor1}
            onChange={handleDate1Change}
            data-e2e="date-1"
            size="md"
            validationState={date1Errors.length > 0 ? 'invalid' : undefined}
            disablePresets={disabledDatePresets}
            readonly={isRfsPending}
          />
        </TradeCaptureValidationWrapper>
      </td>

      <td className="p-0" style={{ opacity: isExcluded ? 0.5 : undefined }}>
        {isSwapLeg(product) && (
          <TradeCaptureValidationWrapper errors={amount2Errors}>
            <SgNumberInput
              size="md"
              value={amount2}
              valuePosition="start"
              maxDecimals={9}
              selectAllOnFocus
              acceleratorsEnabled
              allowNegative={false}
              onChange={handleAmount2Change}
              unit={negotiatedCurrency}
              unitBackgroundColor={negotiatedCurrencyIndex === 1 ? undefined : 'warning'}
              unitPosition="end"
              unitTabNoIndex
              onUnitClick={handleToggleCurrency}
              data-e2e="amount-2"
              state={amount2Errors.length > 0 ? 'invalid' : undefined}
              readOnly={isRfsPending}
            />
          </TradeCaptureValidationWrapper>
        )}
      </td>

      <td className="p-0" style={{ opacity: isExcluded ? 0.5 : undefined }}>
        {isSwapLeg(product) && (
          <TradeCaptureValidationWrapper errors={date2Errors}>
            <DatePickerWithTenorNext
              date={date2}
              tenor={dateTenor2}
              onChange={handleDate2Change}
              data-e2e="date-2"
              size="md"
              validationState={date2Errors.length > 0 ? 'invalid' : undefined}
              disablePresets={disabledDatePresets}
              readonly={isRfsPending}
            />
          </TradeCaptureValidationWrapper>
        )}
      </td>
    </tr>
  );
};
